'use client';

import { useEffect } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

const LOAD_MAX_ATTEMPTS = 25;
const LOAD_RETRY_DELAY_MS = 120;
const LOAD_SCROLL_DELAY_MS = 1500;
const SOFT_SCROLL_DELAY_MS = 1000;
const SOFT_NAV_MAX_ATTEMPTS = 30;
const SOFT_NAV_RETRY_DELAY_MS = 100;

export function HashAnchorScroller() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const timeoutIds: Array<ReturnType<typeof setTimeout>> = [];

    function scrollToCurrentHash() {
      const target = getTargetElementFromHash(window.location.hash);

      if (!target) {
        return;
      }

      target.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      });
    }

    function scrollAfterLoadWithDelay() {
      const initialHash = window.location.hash;

      if (!initialHash || initialHash === '#') {
        return;
      }

      let attempts = 0;

      const tryFindTarget = () => {
        const target = getTargetElementFromHash(initialHash);

        if (target) {
          timeoutIds.push(
            setTimeout(() => {
              // Only scroll if hash did not change meanwhile.
              if (window.location.hash === initialHash) {
                target.scrollIntoView({
                  behavior: 'auto',
                  block: 'start',
                });
              }
            }, LOAD_SCROLL_DELAY_MS),
          );
          return;
        }

        if (attempts < LOAD_MAX_ATTEMPTS - 1) {
          attempts += 1;
          timeoutIds.push(setTimeout(tryFindTarget, LOAD_RETRY_DELAY_MS));
        }
      };

      tryFindTarget();
    }

    window.addEventListener('hashchange', scrollToCurrentHash);
    window.addEventListener('load', scrollAfterLoadWithDelay);
    scrollAfterLoadWithDelay();

    return () => {
      window.removeEventListener('hashchange', scrollToCurrentHash);
      window.removeEventListener('load', scrollAfterLoadWithDelay);

      for (const timeoutId of timeoutIds) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  useEffect(() => {
    // Handle App Router soft navigations like /imprint -> /#about-us
    // where hash/path/content may settle in different ticks.
    let attempts = 0;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const tryScrollAfterSoftNavigation = () => {
      const hash = window.location.hash;
      const target = getTargetElementFromHash(hash);

      if (target) {
        target.scrollIntoView({
          behavior: 'auto',
          block: 'start',
        });
        return;
      }

      if (attempts < SOFT_NAV_MAX_ATTEMPTS - 1) {
        attempts += 1;
        timeoutId = setTimeout(
          tryScrollAfterSoftNavigation,
          SOFT_NAV_RETRY_DELAY_MS,
        );
      }
    };

    timeoutId = setTimeout(tryScrollAfterSoftNavigation, SOFT_SCROLL_DELAY_MS);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [pathname, searchParams?.toString()]);

  return null;
}

function getTargetElementFromHash(hash: string) {
  if (!hash || hash === '#') {
    return null;
  }

  const decodedHash = decodeURIComponent(hash.slice(1));
  const elementById = document.getElementById(decodedHash);

  if (elementById) {
    return elementById;
  }

  return getElementByHashSelector(hash);
}

function getElementByHashSelector(hash: string) {
  try {
    return document.querySelector(hash);
  } catch {
    return null;
  }
}
